import { IntlShape } from 'react-intl';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import { InfoItemProps } from '@designsystem/stories/02-atoms/Info';
import { TagProps } from '@designsystem/stories/02-atoms/Tag';
import { CardProps } from '@designsystem/stories/03-molecules/Card';
import { CardStyle, EventContent, TypeNormalizer, ImageSizes } from '../types';
import { stringOrUndefined, findNextOccasion } from '../utils';
import { normalizeContent, normalizeLink } from '../normalizeContent';
import { messages } from '../messages';
import dateFormat from '@fhnw/components/DateFormat/dateFormat';
import type { OccasionProps } from '@fhnw/types/occasion';

/**
 * Normalizes event-type content (InfoEvent, FHNWEvent)
 */
export const normalizeEventCard: TypeNormalizer = (
  item,
  normalized,
  intl,
  university,
  imageSizes,
) => {
  let eventItem = item as EventContent;
  let occasion: OccasionProps | null = null;
  const style: CardStyle = normalized?.style;

  // Handle Occasion type items
  if (eventItem?.['@type'] === 'Occasion') {
    occasion = eventItem as unknown as OccasionProps;
    if (eventItem?.parent) {
      eventItem = eventItem.parent as unknown as EventContent;
      // Get event card data and merge with current normalized data
      const eventCard = createBaseEventCard(eventItem, intl, imageSizes);
      normalized = {
        ...normalized,
        ...eventCard,
        type: normalized.type, // Keep original type
      };
      try {
        normalized.link = normalizeLink(eventItem);
      } catch {
        // Ignore link transformation errors
      }
    }
  } else {
    occasion = findNextOccasion(eventItem?.occasions, item as Content);
  }

  // Set category from event type
  if (eventItem?.event_type) {
    normalized.category = stringOrUndefined(eventItem.event_type, intl);
  }

  // Add occasion title if available
  if (occasion?.occasion_title) {
    normalized.subtitle = occasion.occasion_title;
  }

  if (style === 'tile') {
    // Build tags
    const tags = buildEventTags(eventItem, occasion, intl);

    if (tags.length > 0) {
      normalized.tags = tags;
    }
  } else {
    // Build info
    const info = buildEventInfo(eventItem, occasion, intl, style);

    if (info.length > 0) {
      normalized.info = info;
    }
  }

  return normalized;
};

/**
 * Creates a base event card from event content
 */
const createBaseEventCard = (
  event: EventContent,
  intl: IntlShape,
  imageSizes?: ImageSizes,
): Partial<CardProps> => {
  const normalizedContent = normalizeContent(event, true, imageSizes);

  // Handle the case where normalizeContent returns a string or object
  if (typeof normalizedContent === 'string') {
    return {
      title: event.title,
      description: normalizedContent,
    };
  }

  const contentObj = normalizedContent as any;
  return {
    title: contentObj?.teaser_title || contentObj?.title || event.title,
    description:
      contentObj?.teaser_text || contentObj?.description || event.description,
    image: contentObj?.image || event.image,
  };
};

/**
 * Builds tags for event cards
 */
const buildEventTags = (
  event: EventContent,
  occasion: OccasionProps | null,
  intl: IntlShape,
): TagProps[] => {
  const tags: TagProps[] = [];

  // Add occasion-specific tags
  if (occasion !== null) {
    // Location tag
    if (occasion?.place_short) {
      tags.push({
        label: occasion.place_short,
      });
    } else if (occasion?.place) {
      tags.push({
        label: occasion.place.replaceAll('\n', ', '),
      });
    }

    // Date tag
    if (occasion?.occasion_start || occasion?.occasion_end) {
      const startDate = occasion?.occasion_start
        ? new Date(occasion.occasion_start)
        : undefined;
      const endDate = occasion?.occasion_end
        ? new Date(occasion.occasion_end)
        : undefined;

      tags.push({
        label: dateFormat({
          start: startDate,
          end: endDate,
          openEnd: occasion?.open_end,
          wholeDay: occasion?.whole_day,
          locale: intl.locale,
        }),
      });
    } else if (occasion?.rolling_intake) {
      tags.push({
        label: intl.formatMessage(messages.RollingIntake),
      });
    }
  }

  return tags;
};

/**
 * Builds info for event cards
 */
const buildEventInfo = (
  event: EventContent,
  occasion: OccasionProps | null,
  intl: IntlShape,
  style: CardStyle,
): InfoItemProps[] => {
  const info: InfoItemProps[] = [];

  // Set category from event type
  if (style === 'table' && event?.event_type) {
    info.push({
      key: intl.formatMessage(messages.Type),
      value: stringOrUndefined(event.event_type, intl),
    });
  }

  // Add occasion-specific tags
  if (occasion !== null) {
    // Date
    if (occasion?.occasion_start || occasion?.occasion_end) {
      const startDate = occasion?.occasion_start
        ? new Date(occasion.occasion_start)
        : undefined;
      const endDate = occasion?.occasion_end
        ? new Date(occasion.occasion_end)
        : undefined;

      info.push({
        key: intl.formatMessage(messages.Date),
        value: dateFormat({
          start: startDate,
          end: endDate,
          openEnd: occasion?.open_end,
          wholeDay: occasion?.whole_day,
          locale: intl.locale,
        }),
      });
    } else if (occasion?.rolling_intake) {
      info.push({
        key: intl.formatMessage(messages.Date),
        value: intl.formatMessage(messages.RollingIntake),
      });
    }

    // Location
    if (occasion?.place_short) {
      info.push({
        key: intl.formatMessage(messages.Venue),
        value: occasion.place_short,
      });
    } else if (occasion?.place) {
      info.push({
        key: intl.formatMessage(messages.Venue),
        value: occasion.place.replaceAll('\n', ', '),
      });
    }
  }

  return info;
};
